import React, { KeyboardEvent, MouseEvent, ReactNode, useCallback, useState } from 'react';
import Icon from 'icons/Icon';
import { AccordionIndicator, AccordionOpenCloseIcon } from './AccordionIndicators';
import DefaultExpandableHeader from '../sar/components/DefaultExpandableHeader';
import DefaultExpandableItem from '../sar/components/DefaultExpandableItem';

const isActivationKey = (key: string): boolean => key === 'Enter' || key === ' ';

const ExpandableGroup = <T,>({
  hasError = false,
  baseKey,
  label,
  items,
  accordion,
  getItemTitle,
  renderItems,
  emptyLabel,
  onAdd,
  renderHeaderDetails,
  renderItemDetails,
  labelClassname,
  getItemHasError,
}: Props<T>) => {
  const expanded = accordion.isExpanded(baseKey);
  const nestedAccordion = useAccordionState();

  const handleKeyboardActivation = (event: KeyboardEvent<HTMLDivElement>, onActivate: () => void): void => {
    if (isActivationKey(event.key)) {
      event.preventDefault();
      onActivate();
    }
  };

  const toggleSection = useCallback((): void => {
    accordion.toggle(baseKey);
  }, [accordion, baseKey]);

  const handleAdd = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      onAdd?.();
      if (!expanded) {
        accordion.toggle(baseKey);
      }
    },
    [onAdd, expanded, accordion, baseKey],
  );

  if (items.length === 0 && onAdd == undefined) {
    const collapsedHeaderDetails = renderHeaderDetails ? (
      renderHeaderDetails({ hasError, label, expanded: false, itemCount: 0 })
    ) : (
      <DefaultExpandableHeader hasError={hasError} label={label} headerLabelClassName={`flex flex-col ${labelClassname}`} />
    );

    return (
      <div className={'flex flex-col'}>
        <div className={'flex w-full rounded-t-xl items-center justify-between pl-10 pr-5 2xl:pr-6 2xl:pl-20 h-[56px] 2xl:h-[58px] text-left border-b-[0.5px]'}>
          <div className={'flex items-center gap-x-3'}>{collapsedHeaderDetails}</div>
          <div className={'flex items-center gap-x-3'}>
            <span className={'rounded-full bg-[#EEF2FF] px-2 py-[2px] text-xs text-[#344054]'}>0</span>
          </div>
        </div>
        <div className={'flex flex-col'}>
          <div className={'rounded-lg border border-dashed border-[#D0D5DD] bg-white px-4 py-3 text-sm text-[#667085]'}>{emptyLabel}</div>
        </div>
      </div>
    );
  }

  const headerDetails = renderHeaderDetails ? (
    renderHeaderDetails({ hasError, label, expanded, itemCount: items.length })
  ) : (
    <DefaultExpandableHeader hasError={hasError} label={label} headerLabelClassName={`flex flex-col ${labelClassname}`} />
  );

  return (
    <div className={'flex flex-col'}>
      <div className={'flex w-full rounded-t-xl items-center justify-between pl-10 2xl:pl-16 pr-5 2xl:pr-6 h-[56px] 2xl:h-[58px] text-left border-b-[0.5px]'}>
        <div
          role={'button'}
          tabIndex={0}
          className={'flex flex-1 items-center justify-between text-left transition-colors hover:text-primary'}
          onClick={toggleSection}
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => handleKeyboardActivation(event, toggleSection)}
        >
          <div className={'flex items-center gap-x-3'}>
            <AccordionOpenCloseIcon expanded={expanded} hasError={hasError} />
            {headerDetails}
          </div>
          <div className={'flex items-center gap-x-3'}>
            {onAdd && (
              <button type={'button'} className={'h-5 w-5 items-center justify-center text-[#344054] hover:bg-f-gray-2 rounded-sm flex'} onClick={handleAdd}>
                <Icon icon={'add'} size={14} />
              </button>
            )}
            <AccordionIndicator expanded={expanded} />
          </div>
        </div>
      </div>
      {expanded && (
        <div className={'flex flex-col'}>
          {items.length === 0 ? (
            <div className={'rounded-lg border border-dashed border-[#D0D5DD] bg-white px-4 py-3 text-sm text-[#667085]'}>{emptyLabel}</div>
          ) : (
            items.map((item, index) => {
              const itemKey = `${baseKey}-item-${index}`;
              const isItemExpanded = nestedAccordion.isExpanded(itemKey);
              const toggleItem = (): void => {
                nestedAccordion.toggle(itemKey);
              };
              const title = getItemTitle(item, index);
              const itemHasError = getItemHasError ? getItemHasError(item, index) : hasError;
              const itemDetails = renderItemDetails ? (
                renderItemDetails({ item, index, expanded: isItemExpanded, title, hasError: itemHasError })
              ) : (
                <DefaultExpandableItem hasError={itemHasError} label={title} itemLabelClassName={'flex flex-col'} />
              );
              return (
                <div key={itemKey} className={'flex flex-col'}>
                  <div
                    role={'button'}
                    tabIndex={0}
                    className={'flex w-full items-center justify-between pr-5 2xl:pr-6 h-[56px] 2xl:h-[58px] text-left pl-16 2xl:pl-[90px] '}
                    onClick={toggleItem}
                    onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => handleKeyboardActivation(event, toggleItem)}
                  >
                    <div className={'flex items-center gap-x-3'}>
                      <AccordionOpenCloseIcon expanded={isItemExpanded} hasError={itemHasError} />
                      {itemDetails}
                    </div>
                    <AccordionIndicator expanded={isItemExpanded} />
                  </div>
                  {isItemExpanded && <div className={'border-t border-[#F2F4F7] bg-white rounded-b-xl'}>{renderItems(item, index)}</div>}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export interface AccordionStateController {
  isExpanded: (key: string) => boolean;
  toggle: (key: string) => void;
}

export const useAccordionState = (): AccordionStateController => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const isExpanded = useCallback((key: string) => expandedKeys.has(key), [expandedKeys]);

  const toggle = useCallback((key: string) => {
    setExpandedKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      if (nextKeys.has(key)) {
        nextKeys.delete(key);
      } else {
        nextKeys.add(key);
      }
      return nextKeys;
    });
  }, []);

  return { isExpanded, toggle };
};

export interface ExpandableGroupHeaderContext {
  hasError?: boolean;
  label: string;
  expanded: boolean;
  itemCount: number;
}

export interface ExpandableGroupItemContext<T> {
  hasError?: boolean;
  item: T;
  index: number;
  expanded: boolean;
  title: string;
}

interface Props<T> {
  hasError?: boolean;
  baseKey: string;
  label: string;
  items: T[];
  accordion: AccordionStateController;
  getItemTitle: (item: T, index: number) => string;
  renderItems: (item: T, index: number) => ReactNode;
  labelClassname?: string;
  emptyLabel: string;
  onAdd?: () => void;
  renderHeaderDetails?: (context: ExpandableGroupHeaderContext) => ReactNode;
  renderItemDetails?: (context: ExpandableGroupItemContext<T>) => ReactNode;
  getItemHasError?: (item: T, index: number) => boolean;
}

export default ExpandableGroup;
